import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';

/**
 * Iskačuća obaveštenja u panelu.
 *
 * Panel do sada nije imao nijedan način da javi da se nešto desilo - prijava
 * problema je stizala u bazu, a čovek je saznavao tek kad sam otvori spisak.
 *
 * Namerno jednostavno: bez biblioteke, bez animacione mašinerije. Obaveštenje
 * ume da bude **kliktabilno** (vodi na tiket) i samo nestaje; kritična ostaju
 * dok se ne zatvore rukom, jer je bolje da smetaju nego da se propuste.
 */

export type Toast = {
  id: string;
  title: string;
  body?: string;
  tone?: 'info' | 'warning' | 'critical';
  /** Klik na obaveštenje - npr. otvaranje tiketa. */
  onClick?: () => void;
};

type ToastApi = { push: (t: Omit<Toast, 'id'>) => void };

const Ctx = createContext<ToastApi>({ push: () => {} });

export const useToasts = () => useContext(Ctx);

const TONE: Record<NonNullable<Toast['tone']>, { border: string; accent: string }> = {
  info: { border: '#1d4ed8', accent: '#60a5fa' },
  warning: { border: '#b45309', accent: '#fbbf24' },
  critical: { border: '#b91c1c', accent: '#f87171' },
};

/** Koliko obaveštenje stoji pre nego što se samo skloni. */
const TTL_MS = 8_000;
/** Više od ovoga postaje zid teksta koji niko ne čita. */
const MAX_VISIBLE = 4;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = `t${++seq.current}`;
    setToasts((prev) => [...prev, { ...t, id }].slice(-MAX_VISIBLE));
    // Kritično ostaje dok se ne zatvori rukom - bolje da smeta nego da se propusti.
    if (t.tone !== 'critical') setTimeout(() => dismiss(id), TTL_MS);
  }, [dismiss]);

  const api = useMemo(() => ({ push }), [push]);

  return (
    <Ctx.Provider value={api}>
      {children}
      <div
        style={{
          position: 'fixed', right: 16, bottom: 16, zIndex: 100,
          display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 380,
        }}
        data-testid="admin-toasts"
      >
        {toasts.map((t) => {
          const tone = TONE[t.tone ?? 'info'];
          return (
            <div
              key={t.id}
              onClick={() => { t.onClick?.(); dismiss(t.id); }}
              data-testid="admin-toast"
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '10px 12px',
                background: '#0f172a',
                border: `1px solid ${tone.border}`,
                borderLeft: `3px solid ${tone.accent}`,
                borderRadius: 8,
                boxShadow: '0 12px 30px rgba(0,0,0,0.45)',
                cursor: t.onClick ? 'pointer' : 'default',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>{t.title}</div>
                {t.body && (
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, overflowWrap: 'anywhere' }}>
                    {t.body}
                  </div>
                )}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); dismiss(t.id); }}
                aria-label="Zatvori obaveštenje"
                style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0 }}
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </Ctx.Provider>
  );
}
